import { format } from 'date-fns';
import { ALLOCATION_MODES } from './constants';

export const draftKey = ({ carrier_id, sku }) => `${carrier_id}::${sku}`;

export const sellableLabel = ({ on_hand_count }, qty) => {
    const sellable = Number(on_hand_count) || 0;
    if (!qty) {
        return `${sellable} sellable`;
    }
    return `${sellable} sellable → ${sellable - qty} left`;
};

export const draftTotals = (draft) => {
    let unitCount = 0;
    draft.forEach(({ qty }) => {
        unitCount += qty;
    });
    return { skuCount: draft.size, unitCount };
};

// Serialized mode pins each chosen box of a serialized SKU by its serial; the pick scan then has to match it
// or confirm a swap. A non-serialized SKU is held by count, so its lines stay unpinned. A chosen box without
// a serial, or one the latest stock read no longer lists, leaves its line unpinned.
export const buildPlaceholderItems = (draft, itemsByKey = new Map()) =>
    Array.from(draft.values()).flatMap((line) => {
        const { sku, description, qty, item_ids } = line;
        const base = {
            sku,
            description,
            quantity: 1,
            is_return: false,
            is_packaged: true,
        };
        if (item_ids?.length) {
            const units = new Map((itemsByKey.get(draftKey(line)) || []).map((unit) => [unit.item_id, unit]));
            return item_ids.map((itemId) => {
                const unit = units.get(itemId);
                const serial = unit?.serialized !== false && unit?.item?.serial_number;
                return serial ? { ...base, serial_number: serial } : { ...base };
            });
        }
        return Array.from({ length: qty }, () => ({ ...base }));
    });

export const filterSkus = (rows, { search, carrierId }) => {
    const needle = (search || '').trim().toLowerCase();
    return rows.filter((row) => {
        if (carrierId && row.carrier_id !== carrierId) {
            return false;
        }
        if (!needle) {
            return true;
        }
        return [row.sku, row.description, row.carrier_name].some((field) =>
            (field || '').toLowerCase().includes(needle)
        );
    });
};

const unitIdentity = ({ item, item_id }) => {
    if (item?.serial_number) {
        return item.serial_number;
    }
    if (item?.line_id) {
        return `Line ${item.line_id}`;
    }
    return `No serial · #${(item_id || '').slice(0, 8)}`;
};

export const serialOptionLabel = (row) => {
    const identity = unitIdentity(row || {});
    const received = row?.received_into_warehouse_time;
    if (!received) {
        return identity;
    }
    const parsed = new Date(received);
    if (Number.isNaN(parsed.getTime())) {
        return identity;
    }
    return `${identity} · received ${format(parsed, 'MMM d')}`;
};

export const groupItemsByDraftKey = (items) =>
    (items || []).reduce((acc, item) => {
        const key = draftKey(item);
        const existing = acc.get(key);
        if (existing) {
            existing.push(item);
        } else {
            acc.set(key, [item]);
        }
        return acc;
    }, new Map());

export const isDraftComplete = (draft, mode, { itemsLoaded = true } = {}) => {
    if (draft.size === 0) {
        return false;
    }
    if (mode !== ALLOCATION_MODES.SERIALIZED) {
        return true;
    }
    if (!itemsLoaded) {
        return false;
    }
    return Array.from(draft.values()).every((line) => (line.item_ids || []).length > 0);
};

// Why the serial options are unavailable, or null when they genuinely are.
const stockLoadIssue = ({ itemsLoaded = true, itemsError = null } = {}) => {
    if (itemsError) {
        return "Couldn't load available stock";
    }
    if (!itemsLoaded) {
        return 'Loading available stock…';
    }
    return null;
};

// What the serial picker says when it has no options to offer. "No sellable
// units" alone claimed the warehouse was empty while the item query was still
// in flight, contradicting the line's own sellable count.
export const serialEmptyText = (state) => stockLoadIssue(state) || 'No sellable units';

export const draftBlockedReason = (draft, mode, { itemsLoaded = true, itemsError = null } = {}) => {
    if (draft.size === 0) {
        return null;
    }
    if (mode !== ALLOCATION_MODES.SERIALIZED) {
        return null;
    }
    const issue = stockLoadIssue({ itemsLoaded, itemsError });
    if (issue) {
        return issue;
    }
    if (!isDraftComplete(draft, mode, { itemsLoaded })) {
        return 'Pick a serial number for every SKU';
    }
    return null;
};
